import { useMutation } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useGT } from 'gt-react';
import { orpc, useInvalidateRpcQueryFn } from '@/api/rpc/client';
import { useToast } from '@/context/ToastContext';
import type {
  As400ImportApplyResponse,
  As400ImportCreateReviewResponse,
} from '@erp/rpc';

/** One file as the import takes it: its name, and its bytes as base64. */
export interface As400ImportUpload {
  fileName: string;
  contentBase64: string;
}

/**
 * Read a file's bytes as base64.
 *
 * Bytes, not text: the AS/400 takes printable ASCII and nothing else, so the
 * server has to see exactly what is in the file to refuse the cell that isn't —
 * and `readAsText` would already have turned an unconvertible byte into
 * `U+FFFD`, which names nothing.
 */
export async function as400ImportFileToBase64(file: File): Promise<string> {
  const buffer = await file.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  let binary = '';
  // Chunked: spreading a few hundred thousand bytes into one `apply` call
  // overflows the argument list.
  const chunkSize = 8192;
  for (let offset = 0; offset < bytes.length; offset += chunkSize) {
    binary += String.fromCharCode(
      ...bytes.subarray(offset, offset + chunkSize),
    );
  }
  return btoa(binary);
}

/**
 * What a set of AS/400 upload files would stage onto a review — every line's
 * disposition and every rejection — without writing any of it.
 *
 * A mutation rather than a query because it carries the files' bytes: there is
 * nothing to cache against, and it is fired when files are chosen rather than on
 * render.
 */
export function useAs400ImportPreview() {
  const onError = useNotReadToast();
  return useMutation({
    ...orpc.inventoryManagement.as400Import.preview.mutationOptions(),
    onError,
  });
}

/** The toast both previews raise when the server can't read the files. */
function useNotReadToast(): (error: Error) => void {
  const { showToast } = useToast();
  const gt = useGT();
  return (error) =>
    showToast({
      message:
        error.message ||
        gt('Those files could not be read.', {
          context: 'fallback toast when AS/400 import files cannot be read',
        }),
      title: gt('Not read', {
        context: 'toast title when AS/400 import files cannot be read',
      }),
      color: 'error',
      icon: 'error',
    });
}

/**
 * Stage the files' cells as edits on the review.
 *
 * Invalidates what a grid save invalidates, plus the publish preview: an import
 * writes the same staged changes through the same service, and it can touch
 * several worksheets at once, so what a publish would send has moved too.
 */
export function useAs400ImportApply() {
  const { showToast } = useToast();
  const gt = useGT();
  const invalidateRpcQueryFn = useInvalidateRpcQueryFn((o) => [
    o.inventoryManagement.products.listAs400Export.key(),
    o.inventoryManagement.products.listRawDataRecords.key(),
    o.inventoryManagement.metadataChangeSets.listEvents.key(),
    o.inventoryManagement.metadataChangeSets.listVersions.key(),
    o.inventoryManagement.as400Publish.preview.key(),
    // A product added by the import changes what the review lists.
    o.inventoryManagement.products.listCatalog.key(),
  ]);

  return useMutation({
    ...orpc.inventoryManagement.as400Import.apply.mutationOptions(),
    onSuccess: (result: As400ImportApplyResponse) => {
      showToast({
        message: gt(
          '{rows} record(s) staged, {added} product(s) added to the review. {skipped} line(s) were skipped.',
          {
            context:
              'toast after AS/400 upload files were staged onto a review',
            rows: result.savedRowCount.toLocaleString(),
            added: result.addedProductCount.toLocaleString(),
            skipped: result.rejectedLineCount.toLocaleString(),
          },
        ),
        title: gt('Loaded into the review', {
          context: 'toast title after an AS/400 import staged its edits',
        }),
        color: 'success',
        icon: 'check',
      });
      invalidateRpcQueryFn();
    },
    onError: (error: Error) => {
      showToast({
        message:
          error.message ||
          gt('Nothing was loaded.', {
            context: 'fallback toast when an AS/400 import stages nothing',
          }),
        title: gt('Not loaded', {
          context: 'toast title when an AS/400 import stages nothing',
        }),
        color: 'error',
        icon: 'error',
      });
    },
  });
}

/**
 * What a batch of upload files would create as a new review — the products it
 * would hold, the items it would add, every line refused — without creating
 * anything. A mutation for the same reason as {@link useAs400ImportPreview}.
 */
export function useAs400ImportNewReviewPreview() {
  const onError = useNotReadToast();
  return useMutation({
    ...orpc.inventoryManagement.as400Import.previewNewReview.mutationOptions(),
    onError,
  });
}

/**
 * Create the review from the batch — all of it or none of it — and land on it.
 */
export function useAs400ImportCreateReview() {
  const { showToast } = useToast();
  const gt = useGT();
  const navigate = useNavigate();
  const invalidateRpcQueryFn = useInvalidateRpcQueryFn((o) => [
    o.inventoryManagement.metadataChangeSets.list.key(),
  ]);

  return useMutation({
    ...orpc.inventoryManagement.as400Import.createReview.mutationOptions(),
    onSuccess: (result: As400ImportCreateReviewResponse) => {
      showToast({
        title: gt('Review created', {
          context: 'toast title after a review was created from AS/400 files',
        }),
        message: gt(
          '{items} new item(s) and {products} existing product(s) loaded from the files.',
          {
            context:
              'toast after a review was created from AS/400 upload files',
            items: result.newItemCount.toLocaleString(),
            products: result.productCount.toLocaleString(),
          },
        ),
        color: 'success',
        icon: 'check',
      });
      invalidateRpcQueryFn();
      void navigate({
        to: '/item-master/reviews/$changeSetId',
        params: { changeSetId: result.changeSetId },
      });
    },
    onError: (error: Error) => {
      showToast({
        message:
          error.message ||
          gt('No review was created.', {
            context:
              'fallback toast when creating a review from AS/400 files fails',
          }),
        title: gt('Not created', {
          context: 'toast title when creating a review from AS/400 files fails',
        }),
        color: 'error',
        icon: 'error',
      });
    },
  });
}
